/* Lovely Cards paid services. One cart coordinator owns both fees. */
(function (root) {
  'use strict';
  const selected = (value) => {
    const text = String(value == null ? '' : value).trim();
    return text !== '' && !/^(no|false|off|0)$/i.test(text);
  };
  function plan(cart, services) {
    const items = cart.items || [];
    const feeIds = new Set(services.map((s) => Number(s.id)).filter(Boolean));
    const updates = {}, additions = [], totals = [];
    for (const service of services) {
      const required = items.reduce((sum, item) => {
        if (feeIds.has(Number(item.variant_id))) return sum;
        return sum + (selected(item.properties?.[service.property]) ? item.quantity : 0);
      }, 0);
      const fees = items.filter((item) => Number(item.variant_id) === Number(service.id));
      if (required && (!service.id || !service.available || !(service.price > 0))) {
        throw new Error(`${service.label} is temporarily unavailable. Please deselect it or contact us before ordering.`);
      }
      if (!fees.length && required) additions.push({ id: Number(service.id), quantity: required });
      fees.forEach((fee, index) => {
        const wanted = index === 0 ? required : 0;
        if (fee.quantity !== wanted) updates[fee.key] = wanted;
      });
      totals.push({ ...service, required, charged: fees.reduce((sum, fee) => sum + fee.quantity, 0), fees });
    }
    return { updates, additions, totals, changed: Object.keys(updates).length > 0 || additions.length > 0 };
  }
  function verify(cart, services) {
    const result = plan(cart, services);
    if (result.changed) throw new Error('Your cart changed. Please review it and try checkout again.');
    for (const service of result.totals) {
      for (const fee of service.fees) {
        if (fee.final_line_price !== service.price * fee.quantity) {
          throw new Error(`${service.label} pricing could not be verified. Please contact us before ordering.`);
        }
      }
    }
    return result;
  }
  function createClient(config, request) {
    let pending = Promise.resolve();
    const queue = (task) => {
      const next = pending.then(task, task);
      pending = next.catch(() => {});
      return next;
    };
    const read = () => request(config.cartJsonUrl);
    async function reconcile(initial) {
      let cart = initial || await read();
      let changed = false;
      // Re-read after mutations: Shopify can cap stock or another tab may change the cart.
      for (let attempt = 0; attempt < 3; attempt++) {
        const next = plan(cart, config.services);
        if (!next.changed) {
          verify(cart, config.services);
          return { cart, changed };
        }
        changed = true;
        if (Object.keys(next.updates).length) await request(config.cartUpdateUrl, { updates: next.updates });
        // Do not automatically retry a timed-out POST: it may already have succeeded.
        if (next.additions.length) await request(config.cartAddUrl, { items: next.additions });
        cart = await read();
      }
      throw new Error('We could not verify the service charges. Please refresh your cart and try again.');
    }
    return {
      read,
      ready: () => pending,
      synchronize: () => queue(() => reconcile()),
      add: (body) => queue(async () => {
        const added = await request(config.cartAddUrl, body);
        const verified = await reconcile();
        if (verified.changed && body.sections?.length) {
          const rendered = await request(config.cartUpdateUrl, { updates: {}, sections: body.sections, sections_url: body.sections_url });
          verify(rendered, config.services);
          added.sections = rendered.sections;
        }
        return added;
      }),
      change: (body) => queue(async () => {
        const changed = await request(config.cartChangeUrl, body);
        const result = await reconcile(changed);
        // Original sections predate any repaired fees.
        if (result.changed && body.sections?.length) {
          const rendered = await request(config.cartUpdateUrl, { updates: {}, sections: body.sections, sections_url: body.sections_url });
          verify(rendered, config.services);
          return rendered;
        } else result.cart.sections = changed.sections;
        return result.cart;
      })
    };
  }
  root.LovelyAddonsCore = { selected, plan, verify, createClient };
  if (typeof document === 'undefined') return;
  const configNode = document.getElementById('lovely-addon-config');
  if (!configNode || root.LovelyAddons) return;
  const config = JSON.parse(configNode.textContent);
  async function request(url, body) {
    const response = await fetch(url, {
      method: body ? 'POST' : 'GET', credentials: 'same-origin', cache: 'no-store',
      headers: { Accept: 'application/json', ...(body ? { 'Content-Type': 'application/json' } : {}) },
      ...(body ? { body: JSON.stringify(body) } : {})
    });
    const data = await response.json();
    if (!response.ok || data.status || data.errors) {
      throw new Error(typeof data.description === 'string' ? data.description : 'Your cart could not be updated. Please refresh and try again.');
    }
    return data;
  }
  const client = createClient(config, request);
  root.LovelyAddons = client;
  function prepareFields() {
    document.querySelectorAll('[data-lovely-paid-option]').forEach((input) => {
      const service = config.services.find((s) => s.key === input.dataset.lovelyPaidOption);
      input.disabled = !(service?.id && service.available && service.price > 0);
      if (input.disabled) input.title = `${service?.label || 'This option'} is temporarily unavailable.`;
    });
    document.querySelectorAll('[data-lovely-addon-checkout="pending"]').forEach((button) => {
      button.disabled = false;
      button.dataset.lovelyAddonCheckout = 'ready';
    });
  }
  prepareFields();
  new MutationObserver(prepareFields).observe(document.body, { childList: true, subtree: true });
  function showError(message, form) {
    const productForm = form?.closest('product-form');
    if (productForm?.handleErrorMessage) {
      productForm.handleErrorMessage(message);
      return;
    }
    const target = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
    if (target) { target.setAttribute('role', 'alert'); target.textContent = message; }
    else window.alert(message);
  }
  document.addEventListener('submit', async (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !/\/cart\/add\/?$/.test(new URL(form.action).pathname)) return;
    const personal = form.querySelector('.lovely-personalize__input');
    const gift = form.querySelector('.lovely-giftwrap__check:not(.lovely-printing__check)');
    const choices = config.services.filter((s) => s.key === 'personalisation' ? personal?.value.trim() : gift?.checked);
    if (!choices.length) return;
    event.preventDefault(); event.stopImmediatePropagation();
    if (form.dataset.lovelyAdding === 'true') return;
    const button = event.submitter || form.querySelector('[type="submit"]');
    if (button?.getAttribute('aria-disabled') === 'true' || button?.disabled) return;
    form.dataset.lovelyAdding = 'true';
    button?.setAttribute('aria-disabled', 'true'); button?.classList.add('loading');
    const spinner = form.querySelector('.loading__spinner');
    spinner?.classList.remove('hidden');
    try {
      for (const choice of choices) {
        if (!choice.id || !choice.available || !(choice.price > 0)) throw new Error(`${choice.label} is temporarily unavailable. Please deselect it before ordering.`);
      }
      const data = new FormData(form);
      const quantity = Number(data.get('quantity') || 1);
      if (!data.get('id') || !Number.isInteger(quantity) || quantity < 1) throw new Error('Please select a valid product quantity.');
      const properties = {};
      data.forEach((value, key) => {
        const match = key.match(/^properties\[(.+)\]$/);
        if (match && typeof value === 'string' && value.trim()) properties[match[1]] = value;
      });
      // Autofill does not always fire the input event that sets the property's name.
      if (personal?.value.trim()) properties.Personalisation = personal.value.trim();
      else delete properties.Personalisation;
      if (gift?.checked) properties['Gift wrap'] = gift.value;
      else delete properties['Gift wrap'];
      const main = { id: Number(data.get('id')), quantity, properties };
      if (data.get('selling_plan')) main.selling_plan = data.get('selling_plan');
      const items = [main, ...choices.map((s) => ({ id: Number(s.id), quantity }))];
      const cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
      cart?.setActiveElement?.(document.activeElement);
      const sections = cart?.getSectionsToRender?.().map((s) => s.section || s.id) || [];
      form.closest('product-form')?.handleErrorMessage?.();
      const result = await client.add({ items, sections, sections_url: window.location.pathname });
      if (cart?.renderContents) {
        cart.classList.remove('is-empty'); cart.renderContents(result); cart.open?.();
      } else window.location.assign(config.cartUrl);
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'product-form', productVariantId: main.id, cartData: result });
      }
    } catch (error) {
      showError(`${error.message} If you retry after a connection problem, check your cart first to avoid adding twice.`, form);
    } finally {
      delete form.dataset.lovelyAdding;
      button?.removeAttribute('aria-disabled'); button?.classList.remove('loading'); spinner?.classList.add('hidden');
    }
  }, true);
  let checkoutBusy = false;
  let cartEditAt = 0;
  document.addEventListener('change', (event) => {
    if (event.target.matches?.('input[data-quantity-line-key]')) cartEditAt = Date.now();
  }, true);
  const pause = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
  async function checkout(event, form, url, button) {
    event.preventDefault(); event.stopImmediatePropagation();
    if (checkoutBusy) return;
    checkoutBusy = true;
    button?.setAttribute('aria-busy', 'true');
    try {
      // Capture typed-but-not-yet-blurred quantities before a render can replace inputs.
      const scope = form || document.getElementById('cart') || document.getElementById('CartDrawer-Form');
      const desired = [];
      scope?.querySelectorAll('input[data-quantity-line-key]').forEach((input) => {
        if (Number(input.value) === Number(input.defaultValue)) return;
        if (!input.checkValidity() || !Number.isInteger(Number(input.value))) throw new Error(input.validationMessage || 'Please enter a valid quantity.');
        desired.push({ id: input.dataset.quantityLineKey, quantity: Number(input.value) });
      });
      // Allow Dawn's debounced quantity handler to start, then await its complete render.
      const deadline = Date.now() + 20000;
      do {
        await pause(100);
        if (Date.now() > deadline) throw new Error('Your cart is still updating. Please wait and try checkout again.');
      } while (Date.now() - cartEditAt < 600 || document.querySelector('.cart__items--disabled'));
      await client.ready();
      let quantityCommitted = false;
      if (desired.length) {
        let current = await client.read();
        for (const change of desired) {
          const item = current.items.find((row) => row.key === change.id);
          if (item && item.quantity !== change.quantity) {
            current = await client.change(change);
            quantityCommitted = true;
          }
        }
      }
      const result = await client.synchronize();
      if (result.changed || quantityCommitted) {
        window.sessionStorage.setItem('lovely-addon-cart-updated', '1');
        window.location.assign(config.cartUrl);
      } else window.location.assign(url);
    } catch (error) { showError(error.message, form); }
    finally { checkoutBusy = false; button?.removeAttribute('aria-busy'); }
  }
  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (event.submitter?.name !== 'checkout' || !form.action || !/\/cart\/?$/.test(new URL(form.action).pathname)) return;
    checkout(event, form, config.checkoutUrl, event.submitter);
  }, true);
  document.addEventListener('click', (event) => {
    const link = event.target.closest?.('a[href]');
    if (!link) return;
    const url = new URL(link.href, window.location.href);
    if (url.origin === window.location.origin && /\/checkout\/?$/.test(url.pathname)) checkout(event, null, url.href, link);
  }, true);
  if (window.sessionStorage.getItem('lovely-addon-cart-updated') === '1') {
    window.sessionStorage.removeItem('lovely-addon-cart-updated');
    const target = document.getElementById('cart-errors');
    if (target) {
      target.setAttribute('role', 'status');
      target.textContent = 'Your selected gift wrap and personalisation charges have been updated. Please review the total, then continue to checkout.';
    }
  }
})(globalThis);
